import { useEffect, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { vw } from "../../lib/utils/responsiveUtils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useGetMainDetail } from "../../@apis/hooks/useMain";
import type { CommunityArticleLikesRequest } from "../../@apis/dto/request/community.request";
import { communityArticleLikes } from "../../@apis/v1/community.service";
import { getQueryClient } from "../../@apis/queryClient";
import { cn } from "../../lib/utils/cn";
import { staticURL } from "../../lib/utils/imageUtils";
import { LOGIN_DIALOG_LABEL } from "../../lib/constants/common.constants";
import { useModalStore } from "../../lib/store/useModalStore";
import { useLoginInfo } from "../../hooks/useLoginInfo";
import Button from "../../components/atoms/button";
import { useDeviceStore } from "../../lib/store/useDeviceStore.tsx";
import AppBar from "../../components/atoms/appbar/index.tsx";
import { toast } from "react-toastify";
import { formatDate } from "../../lib/utils/dateUtils";
import { useGeoStore } from "../../lib/store/useGeoStore";

const IOS_STORE_URL = "https://apps.apple.com/app/id6749922967";

/** http://, https:// 또는 www.로 시작하는 URL. 끝의 구두점은 링크에서 제외합니다. */
const URL_REGEX = /(?:https:\/\/|http:\/\/|www\.)[^\s<>"'`]+/gi;

function toLinkHref(url: string) {
  if (/^https:\/\//i.test(url) || /^http:\/\//i.test(url)) {
    return url;
  }
  return `https://${url}`;
}

function linkifyText(text: string) {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_REGEX)) {
    const raw = match[0];
    const index = match.index ?? 0;
    const trailing = raw.match(/[.,;:!?)>\]]+$/)?.[0] ?? "";
    const url = trailing ? raw.slice(0, -trailing.length) : raw;

    if (!url) continue;

    if (index > lastIndex) {
      nodes.push(text.slice(lastIndex, index));
    }

    const href = toLinkHref(url);
    nodes.push(
      <a
        key={`${index}-${url}`}
        href={href}
        target="_blank"
        rel="noreferrer"
        className="underline text-[#e5002b] break-all"
      >
        {url}
      </a>,
    );

    if (trailing) {
      nodes.push(trailing);
    }

    lastIndex = index + raw.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function getStoreUrlIfDeeplinkFallback(): string | null {
  if (new URLSearchParams(location.search).get("_deeplink") !== "1")
    return null;
  const ua = navigator.userAgent;
  const inWebView =
    !!(window as any).ReactNativeWebView ||
    !!(window as any).webkit?.messageHandlers?.reactNativeWebView;
  if (inWebView) return null;
  if (/iP(hone|od|ad)/.test(ua)) return IOS_STORE_URL;
  return null;
}

/** 현재 사용자의 디바이스가 Android 인지 확인합니다. */
export const isAos = (userAgent?: string) =>
  /android/i.test(userAgent ?? navigator.userAgent);

/** 현재 사용자의 디바이스가 iOS 인지 확인합니다. */
export const isIos = (userAgent?: string) =>
  /iphone/i.test(userAgent ?? navigator.userAgent) ||
  /ipad/i.test(userAgent ?? navigator.userAgent);

/** 네이티브 앱 WebView 내부인지 확인합니다. */
function isInNativeWebView() {
  const isIosWebView = !!(window as any).webkit?.messageHandlers?.[
    "onJsLoadComplete"
  ];
  const isAosWebView = !!(window as any).voicepark;
  return isIosWebView || isAosWebView;
}

export default function StoreDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const { isLoggedIn } = useLoginInfo();
  const { deviceType } = useDeviceStore();
  // 검색/홈에서 저장해 둔 마지막 좌표(없을 수 있음)
  const coords = useGeoStore((s) => s.coords);

  // const isBookmark = false;

  useEffect(() => {
    // 외부(parkgolf.or.kr)에서 딥링크 버튼으로 진입 + 앱 미설치인 경우 설치 안내 모달 표시
    const storeUrl = getStoreUrlIfDeeplinkFallback();
    if (!storeUrl) return;

    // 새로고침 시 모달 재오픈 방지를 위해 _deeplink 쿼리 제거
    const url = new URL(window.location.href);
    url.searchParams.delete("_deeplink");
    window.history.replaceState({}, "", url.toString());

    const isMobileWeb = (isAos() || isIos()) && !isInNativeWebView();

    if (!isMobileWeb) return;

    openModal("CONFIRM_DIALOG", {
      title: "앱 설치 안내",
      content:
        "보이스파크 앱이 설치되어 있지 않습니다.\n앱 스토어로 이동하시겠어요?",
      rightTitle: "설치하러 가기",
      onClick: () => {
        window.location.href = storeUrl;
      },
    });
  }, [openModal]);

  const { data: golfParkInfo, isLoading } = useQuery({
    ...useGetMainDetail(id as string, {
      articlePageSize: 3,
      noticePageSize: 1,
      // 저장된 좌표가 있을 때만 전달(없으면 미전달)
      ...(coords && { lat: coords.latitude, lon: coords.longitude }),
    }),
  });
  const { golfPark, noticeList, articleList } = golfParkInfo?.data || {};

  const likeMutation = useMutation({
    mutationFn: (form: CommunityArticleLikesRequest) =>
      communityArticleLikes(form),
    onSuccess: (res) => {
      const { success, error } = res;

      if (success) {
        getQueryClient().invalidateQueries({
          queryKey: ["parkgolf-main-detail", id],
        });
      } else {
        alert(error?.message);
      }
    },
  });

  const handleCopyAddress = async () => {
    if (!golfPark?.address) return;
    try {
      await navigator.clipboard.writeText(golfPark.address);
      toast("복사되었습니다.");
    } catch {
      toast("복사에 실패했습니다.");
    }
  };

  const handleReservation = () => {
    const resveUrl = golfPark?.resveUrl;

    if (!resveUrl) {
      return openModal("ALERT_DIALOG", {
        subTitle:
          "해당 파크골프장은\n현재 온라인 예약이 제공되고 있지 않습니다.",
      });
    }

    const data = { url: resveUrl };
    const jsonString = JSON.stringify(data);

    if (resveUrl) {
      const action = {
        ios: () => {
          const handler = window.webkit?.messageHandlers?.openSystemBrowser;
          if (handler) {
            handler.postMessage(jsonString);
          } else {
            window.open(data.url, "_blank");
          }
        },
        android: () => {
          if (window.VoicePark?.openSystemBrowser) {
            window.VoicePark.openSystemBrowser(jsonString);
          } else {
            window.open(data.url, "_blank");
          }
        },
        web: () => {
          window.open(data.url, "_blank");
        },
      };

      const execute = action[deviceType] || action.web();
      execute();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <AppBar
        title="골프장 상세"
      // right={
      //   <img
      //     src={
      //       isBookmark ? "/icons/ic-star-filled.png" : "/icons/ic-star.png"
      //     }
      //     className="size-[24px] cursor-pointer"
      //     role="button"
      //     onClick={() => toast("즐겨찾기에 추가되었습니다.")}
      //   />
      // }
      />
      {/* 상단 이미지 */}
      <div
        className="w-full aspect-[393/220] bg-white bg-cover bg-center bg-no-repeat relative"
        style={{
          backgroundImage: isLoading
            ? undefined
            : `url(${golfPark?.imageUrl
              ? staticURL(golfPark.imageUrl)
              : "/images/img-login-logo.webp"
            })`,
        }}
      >
        {/* 좋아요 버튼 */}
        <div
          className={cn(
            "absolute left-[12px] bottom-[12px] z-20 flex-row gap-[10px] rounded-full px-[14px] py-[10px] cursor-pointer",
            golfPark?.likeItemId
              ? "bg-[#e5002b] text-white"
              : "border bg-white text-black ",
          )}
          onClick={() => {
            if (!isLoggedIn)
              return openModal("CONFIRM_DIALOG", {
                title: LOGIN_DIALOG_LABEL.title,
                content: LOGIN_DIALOG_LABEL.content,
                onClick: () => navigate("/login"),
              });
            likeMutation.mutate({
              likeItemCode: id as string,
              likeItemType: "GOLFPARK",
            });
          }}
          style={{ marginTop: vw(309) }}
        >
          <img className="size-5" src="/icons/ic-heart-pink.webp" />
          <span className={cn("font-[500]")}>좋아요</span>
          <span className={cn("font-[500] text-sm")}>
            {golfPark?.likeCount}개
          </span>
        </div>
      </div>

      {/* 골프장 정보 */}
      <div className="p-[20px] bg-white">
        <div className="space-y-[12px]">
          <span className="text-[#a2845e] text-xl font-medium px-[8px] py-[2px] bg-[#fff5d9] rounded-[5px] inline-block">
            {golfPark?.area}
          </span>

          <div>
            {golfPark?.distance && (
              <small className="text-[#8e8e93] font-medium text-sm mb-[4px] inline-block">
                내 위치로부터 {golfPark.distance}km
              </small>
            )}
            <h1 className="font-medium text-[1.75rem]">{golfPark?.name}</h1>
          </div>
        </div>
      </div>
      {/* divide */}
      <div className="bg-[#f2f2f7] h-[5px] w-full" />

      <section className="p-[20px] bg-white">
        {/* 골프장 상세 정보 */}
        <div className="space-y-[8px] pb-[16px]">
          {/* 주소 */}
          {golfPark?.address && (
            <div className="flex items-center gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">주소</span>
              <p>
                {golfPark.address}
                <span
                  className="underline text-xs text-[#8e8e93] ml-[4px] cursor-pointer"
                  role="button"
                  onClick={handleCopyAddress}
                >
                  복사
                </span>
              </p>
            </div>
          )}

          {/* 연락처 */}
          {golfPark?.telNum && (
            <div className="flex items-start gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">연락처</span>
              <div>
                <p>{golfPark.telNum}</p>
                {golfPark?.telNum2 && <p>{golfPark.telNum2}</p>}
                {golfPark?.telNum3 && <p>{golfPark.telNum3}</p>}
                {golfPark?.telNum4 && <p>{golfPark.telNum4}</p>}
                {golfPark?.telNum5 && <p>{golfPark.telNum5}</p>}
              </div>
            </div>
          )}

          {/* 운영시간 */}
          {golfPark?.operatingHours && (
            <div className="flex items-center gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">운영시간</span>
              <p>{golfPark.operatingHours}</p>
            </div>
          )}
          {/* 휴장일 */}
          {golfPark?.closedDay && (
            <div className="flex items-center gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">휴장일</span>
              <p>{golfPark.closedDay}</p>
            </div>
          )}
          {/* 예약방법 */}
          {golfPark?.resveMethod && (
            <div className="flex items-center gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">예약방법</span>
              <p>{golfPark.resveMethod}</p>
            </div>
          )}

          {/* 예약사이트 */}
          {golfPark?.resveUrl && (
            <div className="flex items-start gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem] shrink-0">
                예약사이트
              </span>
              <p className="flex min-w-0 items-start gap-x-[4px]">
                <img
                  src="/icons/ic-link.png"
                  alt="link"
                  className="size-4 shrink-0"
                />
                <a
                  rel="noreferrer"
                  href={golfPark.resveUrl}
                  className="underline text-[#007aff] break-all"
                  target="_blank"
                >
                  {golfPark.resveUrl}
                </a>
              </p>
            </div>
          )}
          {/* 이용요금 */}
          {golfPark?.feeType && (
            <div className="flex gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">이용요금</span>
              <div>
                <p>{golfPark.feeType}</p>
                {golfPark?.feeDetail && <p>{golfPark.feeDetail}</p>}
              </div>
            </div>
          )}

          {/* 홀 수 */}
          {golfPark?.holl && (
            <div className="flex items-center gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">홀 수</span>
              <p>{golfPark.holl}홀</p>
            </div>
          )}

          {/* 시설정보 */}
          {golfPark?.facilityInfo && (
            <div className="flex items-center gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">시설정보</span>
              <p>{golfPark.facilityInfo}</p>
            </div>
          )}

          {/* 메모 */}
          {golfPark?.memo && (
            <div className="flex gap-x-[8px] text-sm font-medium">
              <span className="text-[#8e8e93] min-w-[4.4rem]">메모</span>
              <div className="bg-[#f2f2f7] rounded-[10px] p-[10px] w-full">
                <p className="whitespace-pre-wrap break-words">
                  {linkifyText(golfPark.memo)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 콘텐츠 */}
        <div>
          {/* 코스정보 */}

          {(golfPark?.golfCourseList.length || 0) > 0 && (
            <div className="space-y-[8px] pb-[16px]">
              <h2 className="text-sm font-semibold">코스정보</h2>
              <div className="space-y-[8px]">
                {golfPark?.golfCourseList.map((course) => (
                  <div
                    key={course.code}
                    className="border-[#e5e5ea] border rounded-[10px] p-[10px]"
                  >
                    {/* 코스/파 정보 */}
                    <div className="flex justify-between font-semibold">
                      <h2 className="inline-flex items-center text-base">
                        {course.name} 코스
                      </h2>
                      <span className="inline-flex items-center bg-[#eaf9ea] rounded-full px-[10px] text-[#227336] text-sm leading-5">
                        Par{" "}
                        {course.holes.reduce((sum, hole) => sum + hole.par, 0)}
                      </span>
                    </div>
                    {/* divide */}
                    <div className="h-[1px] bg-[#227336] my-[8px]"></div>

                    {/* 홀 */}
                    <div className="grid grid-cols-9 gap-x-[4px]">
                      {course.holes.map((hole) => (
                        <div
                          key={hole.id}
                          className="flex flex-col items-center gap-y-[6px]"
                        >
                          <span className="bg-[#f2f2f7] text-[#8e8e93] text-[12px] font-medium w-full py-[2px] text-center rounded-[5px]">
                            {hole.no}홀
                          </span>
                          <span className="font-semibold text-[14px]">
                            P{hole.par}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 공지사항 */}
          <div className="space-y-[12px] py-[16px] border-t border-[#f2f2f7]">
            <div
              className="flex justify-between items-center"
              onClick={() => navigate(`/notice?store=${id}`)}
            >
              <div className="flex items-center gap-x-[8px]">
                <h2 className="text-sm font-semibold">공지사항</h2>
                <span className="text-[#8e8e93] text-xs">
                  {golfPark?.noticeCount || 0}개
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-[#8e8e93] text-xs">더보기</span>
                <img
                  className="size-6"
                  src="/icons/ic-arrow-right.png"
                  alt="더보기"
                />
              </div>
            </div>
            {noticeList?.[0] ? (
              <div
                className="rounded-[10px] bg-[#f2f2f7] p-[10px] space-y-[4px]"
                onClick={() => navigate(`/notice/${noticeList[0].id}`)}
              >
                <small className="text-[#8e8e93] font-medium text-xs block">
                  {noticeList[0].updateDt &&
                    formatDate(noticeList[0].updateDt, "YYYY년 MM월 DD일")}
                </small>
                <h3 className="font-semibold text-sm truncate">
                  {noticeList[0].title}
                </h3>
                <p className="text-[#8e8e93] font-semibold text-sm line-clamp-2">
                  {noticeList[0].content}
                </p>
              </div>
            ) : (
              <div className="min-h-[122px] flex items-center justify-center">
                <p className="text-[#8e8e93] font-medium text-xs">
                  등록된 공지사항이 없습니다.
                </p>
              </div>
            )}
          </div>

          {/* 커뮤니티 */}
          <div className="space-y-[12px] pt-[16px] border-t border-[#f2f2f7]">
            <div
              className="flex justify-between items-center"
              onClick={() => navigate(`/community?store=${id}`)}
            >
              <div className="flex items-center gap-x-[8px]">
                <h2 className="text-sm font-semibold">커뮤니티</h2>
                <span className="text-[#8e8e93] text-xs">
                  {golfPark?.articleCount || 0}개
                </span>
              </div>
              <div className="flex items-center">
                <span className="text-[#8e8e93] text-xs">더보기</span>
                <img
                  className="size-6"
                  src="/icons/ic-arrow-right.png"
                  alt="더보기"
                />
              </div>
            </div>
            {(articleList?.length ?? 0) > 0 ? (
              <div className="space-y-[8px]">
                {/* 커뮤니티 카드 */}
                {(articleList || []).map((article) => (
                  <div
                    key={article.id}
                    className="rounded-[10px] bg-[#f2f2f7] p-[10px]"
                    onClick={() => navigate(`/community/${article.id}`)}
                  >
                    <div className="space-y-[8px]">
                      {/* 위치 */}
                      <div className="flex items-center gap-x-[4px]">
                        <img
                          className="size-5"
                          src="/icons/ic-golf-info-gray.png"
                          alt="golf icon"
                        />
                        <p className="truncate text-xs font-medium">
                          {article.golfParkName}
                        </p>
                      </div>

                      {/* 닉네임/콘텐츠 */}
                      <div>
                        {/* 닉네임 */}
                        <div className="flex items-center gap-x-[4px] justify-between">
                          {/* 닉네임 */}
                          <h3 className="font-semibold text-sm truncate">
                            {article.nickname}
                          </h3>
                          {/* 날짜 */}
                          <span className="text-[#8e8e93] text-xs font-medium">
                            {formatDate(article.updateDt, "YYYY년 MM월 DD일")}
                          </span>
                        </div>
                        {/* divide */}
                        <div className="my-[12px] h-[1px] bg-[#d1d1d6]" />

                        {/* 콘텐츠 */}
                        <div className="space-y-[4px]">
                          {/* 제목 */}
                          <h3 className="font-semibold text-sm truncate">
                            {article.title}
                          </h3>
                          <p className="font-semibold text-sm text-[#8e8e93] line-clamp-2">
                            {article.content}
                          </p>
                        </div>
                      </div>

                      {/* 좋아요/댓글 */}

                      <div className="flex items-center justify-end py-[2px]">
                        {/* 좋아요 */}
                        <div className="flex items-center gap-x-[4px]">
                          <img
                            className="size-4"
                            src="/icons/ic-heart.png"
                            alt="like icon"
                          />
                          <span className="text-[#8e8e93] font-semibold text-xs">
                            {article.likeCount}개
                          </span>
                        </div>
                        {/* divide */}
                        <div className="mx-[12px] h-4 w-[1px] bg-[#e5e5ea]" />

                        {/* 댓글 */}
                        <div className="flex items-center gap-x-[4px]">
                          <img
                            className="size-4"
                            src="/icons/ic-comment.png"
                            alt="comment icon"
                          />
                          <span className="text-[#8e8e93] font-semibold text-xs">
                            {article.commentCount}개
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="min-h-[122px] flex items-center justify-center">
                <p className="text-[#8e8e93] font-medium text-xs text-center">
                  아직 등록된 게시글이 없습니다.
                  <br />첫 번째 게시글을 남겨보세요!
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 타이틀/보조 텍스트 */}
      {/* <section className="bg-black text-white flex flex-col px-[15px] relative z-10 pb-18">
        <p className="text-2xl font-bold break-keep whitespace-pre-line break-words leading-normal">
          {golfPark?.name}
        </p>

        <div className="flex items-start gap-[10px]">
          <div
            className="mt-[24px] rounded-full bg-[#333] flex items-center justify-center shrink-0"
            style={{ width: vw(48), height: vw(48) }}
          >
            <img
              src="/icons/ic-place.webp"
              alt="주소 아이콘"
              style={{ width: vw(20), height: vw(20) }}
            />
          </div>

          <span className="mt-[25px] font-[500] text-[#fff] leading-[1.5] text-sm">
            {golfPark?.address}
            <br />
            <span>{golfPark?.name}</span>
          </span>
        </div>
      </section> */}

      {/* 카드영역 */}
      {/* <section className="-mt-10 relative z-20 pb-[80px]">
        <div className="bg-white rounded-t-[18px] overflow-hidden">
          <div className="flex items-center justify-between px-[15px] pt-6">
            <span className="mt-4 pb-[14px] font-bold leading-[1.5px] text-left text-[#222]">
              공지사항
            </span>
            <img
              src="/icons/ic-go.webp"
              style={{ width: vw(7), height: vw(12) }}
              onClick={() => navigate(`/notice?store=${id}`)}
            />
          </div>

          <div className="px-[15px] py-[14px] space-y-3">
            {noticeList?.length === 0 && (
              <div className="flex-row w-full font-[500] text-[#ccc] text-center">
                <span className="w-full text-sm">
                  등록된 공지사항이 없습니다.
                </span>
              </div>
            )}
            {(noticeList || []).map((item) => {
              return (
                <div
                  key={item.id}
                  onClick={() => navigate(`/notice/${item.id}`)}
                  className={cn(
                    `relative grid grid-cols-[1fr_auto] items-center bg-[#f5f5f5] rounded-[15px] p-[19px] mb-[15px] cursor-pointer`,
                    item.fileList.length > 0 && "gap-[37px]",
                  )}
                >
                  <div className="min-w-0">
                    <p className=" font-[500] text-[#222] leading-[150%] truncate">
                      {item.title}
                    </p>
                    <p className=" font-[500] text-[#888] whitespace-pre-line line-clamp-3 text-sm leading-[150%]">
                      {item.content}
                    </p>
                  </div>

                  {item.fileList.length > 0 && (
                    <div
                      className="rounded-[10px] overflow-hidden"
                      style={{ width: vw(69), height: vw(69) }}
                    >
                      <img
                        src={staticURL(item.fileList[0].fileUrl)}
                        alt="공지 썸네일"
                        className="rounded-[10px] shrink-0 mt-[5px] object-cover"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className="flex items-center justify-between px-[15px] pt-[18px] cursor-pointer"
            onClick={() => navigate(`/community?store=${id}`)}
          >
            <div className="flex items-baseline gap-[9px]">
              <span className="mt-[10px] pb-[14px] font-bold leading-[1.5px] text-left text-[#222]">
                커뮤니티
              </span>
              <span className="text-[#888] text-sm">
                {golfPark?.articleCount}개
              </span>
            </div>
            <img
              src="/icons/ic-go.webp"
              style={{ width: vw(6), height: vw(14) }}
            />
          </div>

          <div className="p-[15px] space-y-[15px]">
            {articleList?.length === 0 && (
              <div className="flex-row w-full font-[500] text-[#ccc] text-center">
                <span className="w-full text-sm">
                  아직 작성된 게시글이 없습니다.
                  <br />첫 번째 게시글을 남겨보세요.
                </span>
              </div>
            )}
            {(articleList || []).map((item, index) => (
              <CommunityCard variant={"gray"} item={item} key={index} />
            ))}
          </div>
        </div>

        <div className="fixed bottom-0 z-50 w-full max-w-[440px] p-[15px] bg-white">
          <Button
            className="w-full"
            variant="black"
            onClick={handleReservation}
            label={"예약 사이트로 이동"}
          />
        </div>
      </section> */}

      {/* 하단 floating 버튼 */}
      <div className="flex-row w-full gap-[10px] sticky bottom-0 left-0 z-50 bg-white p-[12px]">
        <Button
          className={cn(
            "w-full py-[11px] text-base font-normal leading-[100%]",
            { "cursor-not-allowed": !golfPark?.resveUrl },
          )}
          disabled={!golfPark?.resveUrl}
          variant={golfPark?.resveUrl ? "black" : "disabled"}
          label={
            golfPark?.resveUrl
              ? "예약사이트로 이동하기"
              : "예약사이트가 없습니다."
          }
          onClick={handleReservation}
        />
      </div>
    </div>
  );
}
